import { LoaderCircle, X } from "lucide-react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { HighlightedText } from "@/shared/components/HighlightedText";
import { useDismiss } from "@/shared/hooks/useDismiss";
import type { Mention } from "@/shared/types/mention";
import { useMentionFullText } from "./use-mention-full-text";

/**
 * O texto INTEIRO da menção num modal flutuante (2026-10-19, pedido da
 * dona no Modo revisão: o foco mostra o trecho de 600 caracteres da
 * lista, e abrir o editor só pra ler a matéria é desvio). Busca o
 * detalhe (`mentionDetail`, que traz `content` em vez do trecho) ao
 * abrir, com o trecho da lista na tela enquanto não chega, e o negrito
 * dos termos da marca calculado pelo servidor sobre o texto completo.
 * Fecha por X, Esc e clique fora; `role="dialog"` segura os atalhos de
 * teclado do Modo revisão enquanto está aberto.
 */
export function MentionFullTextDialog({
  mention,
  token,
  onClose
}: {
  mention: Mention;
  token: string;
  onClose: () => void;
}) {
  // A busca tem dono único (`useMentionFullText`), o mesmo do card da lista.
  const { full: detail, error, load } = useMentionFullText(mention, token);

  useDismiss(true, () => onClose());

  useEffect(() => {
    void load();
  }, [load]);

  const shown = detail ?? mention;
  const untitled = shown.title === "Publicação sem título";

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container mention-fulltext"
        role="dialog"
        aria-modal="true"
        aria-label="Texto completo da menção"
        onClick={event => event.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <span className="eyebrow">{shown.source}</span>
            <h2>{untitled ? "Texto completo" : <HighlightedText text={shown.title} spans={shown.highlights.title}/>}</h2>
          </div>
          <button type="button" className="icon-button" aria-label="Fechar" onClick={onClose}>
            <X size={16} aria-hidden="true"/>
          </button>
        </header>
        <div className="modal-body mention-fulltext-body">
          {error ? (
            <p className="form-error-alert" role="alert">{error}</p>
          ) : (
            <>
              <p className="mention-fulltext-text">
                <HighlightedText text={shown.excerpt} spans={shown.highlights.content}/>
              </p>
              {!detail && (
                <p className="mention-fulltext-loading" role="status">
                  <LoaderCircle size={14} className="spin" aria-hidden="true"/> Carregando o texto inteiro…
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
